import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Branded confirm dialog — replaces window.confirm (the native macOS sheet
 * looked like it belonged to another app). Portal-mounted above everything
 * (the workbench modal sits at z-[100]); Escape and backdrop-click cancel;
 * focus lands on Cancel so Enter can never accidentally confirm a
 * destructive action.
 */
const ConfirmDialog = ({
  open,
  title,
  body,
  details, // optional node rendered below the body (checklists, breakdowns)
  confirmLabel,
  cancelLabel,
  danger = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    cancelRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel?.();
      }
    };
    // Capture phase so the workbench modal's own Escape handler (close the
    // whole modal) never sees the key while this dialog is up.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onCancel]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="bg-black/40 fixed inset-0 z-[200] flex items-center justify-center px-4 backdrop-blur-[2px]"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onCancel?.();
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            role="alertdialog"
            aria-modal="true"
            aria-label={title}
            className="w-full max-w-sm rounded-[16px] bg-white p-5 shadow-2xl"
          >
            <div className="flex items-start gap-3">
              <span
                className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
                  danger ? "bg-[#FEF2F2]" : "bg-[#FFF3ED]"
                }`}
              >
                {danger ? (
                  <AlertTriangle size={17} className="text-[#DC2626]" />
                ) : (
                  <HelpCircle size={17} className="text-[#E07A3A]" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-[#111827]">
                  {title}
                </p>
                <p className="mt-1 text-[13px] leading-relaxed text-[#6B7280]">
                  {body}
                </p>
                {details}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                ref={cancelRef}
                onClick={onCancel}
                className="rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E07A3A]/45"
              >
                {cancelLabel ?? t("common.actions.cancel")}
              </button>
              <button
                onClick={onConfirm}
                className={`rounded-[10px] px-4 py-2 text-sm font-semibold text-white transition duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
                  danger
                    ? "bg-[#DC2626] hover:bg-[#B91C1C] focus-visible:ring-[#DC2626]/45"
                    : "hover:opacity-95 focus-visible:ring-[#E07A3A]/45"
                }`}
                style={
                  danger
                    ? undefined
                    : {
                        background:
                          "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                      }
                }
              >
                {confirmLabel ?? t("common.actions.confirm")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ConfirmDialog;
